import React from "react";
import moment from "moment";
import { TimePicker } from "../../components";
import { FilterDatePicker } from "components/DatePicker/SingleDatePicker";
import { Input } from "semantic-ui-react";
import { ModifySegmentInstruction } from "../utils";
import BaseReactSelectDropdown from "components/dropdowns/reactselect/BaseReactSelectDropdown";
import RemovableTrainStation from "components/Removables/RemovableTrainStation";

interface Props {
  segment: ModifySegmentInstruction;
  onSegmentChange: (segment: ModifySegmentInstruction) => void;
  startStationEmpty: boolean;
  endStationEmpty: boolean;
}

const SegmentEdit = (props: Props) => {

  const onTimeChange = (selectedTimeMoment: moment.Moment, current: moment.Moment, onChange: (arrival: moment.Moment) => void) => {
    const newMoment = current.clone().startOf("day")
      .add(selectedTimeMoment.hours(), "hours")
      .add(selectedTimeMoment.minutes(), "minutes");

    onChange(newMoment);
  };

  const onSegmentChange = React.useCallback((newSegment: Partial<ModifySegmentInstruction>) => {
    const newProps = { ...props.segment, ...newSegment };
    props.onSegmentChange(newProps);
  }, [props.segment]);

  const onStartTimeChange = React.useCallback((selectedTimeMoment: moment.Moment) => {
    onTimeChange(selectedTimeMoment,
      moment.utc(props.segment.startLocal),
      (newMoment) => onDateChange(newMoment, "startLocal")
    );
  }, [onSegmentChange]);

  const onEndTimeChange = React.useCallback((selectedTimeMoment: moment.Moment) => {
    onTimeChange(selectedTimeMoment,
      moment.utc(props.segment.endLocal),
      (newMoment) => onDateChange(newMoment, "endLocal")
    );
  }, [onSegmentChange]);


  const onDateChange = React.useCallback((newDate: moment.Moment, startOrEnd: "startLocal" | "endLocal") => {
    let updateBoth = false;
    const other: "startLocal" | "endLocal" = startOrEnd === "startLocal" ? "endLocal" : "startLocal";

    if (startOrEnd === "startLocal") {
      const oldEnd = moment.utc(props.segment.endLocal);
      if (newDate.isAfter(oldEnd)) {
        updateBoth = true;
      }
    } else {
      const oldStart = moment.utc(props.segment.startLocal);
      if (newDate.isBefore(oldStart)) {
        updateBoth = true;
      }
    }

    onSegmentChange({
      [startOrEnd]: newDate.toDate(),
      [other]: updateBoth ? newDate.toDate() : props.segment[other]
    });
  }, [props.segment]);

  const onStartStationChanged = React.useCallback((station: server.dto.TrainStation) => {
    onSegmentChange({
      startStation: station,
      startStationId: station?.id
    });
  }, [onSegmentChange]);

  const onEndStationChanged = React.useCallback((station: server.dto.TrainStation) => {
    onSegmentChange({
      endStationId: station?.id,
      endStation: station
    });
  }, [onSegmentChange]);

  const { segment } = props;
  const start = React.useMemo(() => moment.utc(segment.startLocal), [segment.startLocal]);
  const end = React.useMemo(() => moment.utc(segment.endLocal), [segment.endLocal]);

  return <table className="s1_table bordergrid gray">
    <tbody>
      <tr>
        <th></th>
        <th style={{ width: 428 }}>{RESX.Train.From}</th>
        <th>{RESX.Train.To}</th>
      </tr>
      <tr>
        <td className="label">
          {RESX.DateTime.Time}
        </td>
        <td>
          <div className="flex">
            <div className="margin-right-small">
              <FilterDatePicker
                dontUpdateOnInputChange
                minDate={moment(serverReference.constants.appointment.minDate)}
                maxDate={moment(serverReference.constants.appointment.maxDate)}
                onChange={d => onDateChange(d, "startLocal")}
                value={start}
              />
            </div>
            <div>
              <MemoizedTimepicker
                value={start}
                onTimeChange={onStartTimeChange}
              />
            </div>
          </div>
        </td>
        <td>
          <div className="flex">
            <div className="margin-right-small">
              <FilterDatePicker
                dontUpdateOnInputChange
                minDate={moment(serverReference.constants.appointment.minDate)}
                maxDate={moment(serverReference.constants.appointment.maxDate)}
                onChange={d => onDateChange(d, "endLocal")}
                value={end}
              />
            </div>
            <div>
              <MemoizedTimepicker
                value={end}
                onTimeChange={onEndTimeChange}
              />
            </div>
          </div>
        </td>
      </tr>

      <tr>
        <td className="label">
          {RESX.Train.Station}
        </td>
        <td>
          <RemovableTrainStation
            error={props.startStationEmpty}
            value={segment.startStation}
            onChange={onStartStationChanged}
            readonly={false}
          />
        </td>
        <td>
          <RemovableTrainStation
            error={props.endStationEmpty}
            value={segment.endStation}
            onChange={onEndStationChanged}
            readonly={false}
          />
        </td>
      </tr>

      <tr>
        <td className="label">{RESX.Train.Platform}</td>
        <td>
          <Input
            style={{ width: 60 }}
            maxLength={serverReference.constants.train.maxPlatformLength}
            value={segment.startPlatform}
            onChange={(_, { value }) => onSegmentChange({ startPlatform: value })} />
        </td>
        <td>
          <Input
            style={{ width: 60 }}
            maxLength={serverReference.constants.train.maxPlatformLength}
            value={segment.endPlatform}
            onChange={(_, { value }) => onSegmentChange({ endPlatform: value })} />
        </td>
      </tr>

      <tr>
        <td className="label">
          {RESX.Train.TrainNumber}
        </td>
        <td colSpan={2}>
          <Input
            style={{ width: 100 }}
            value={segment.trainNumber || ""}
            maxLength={serverReference.constants.train.maxTrainNumberLength}
            onChange={(_, { value }) => onSegmentChange({ trainNumber: value })} />
        </td>
      </tr>

      <tr>
        <td className="label">
          {RESX.Train.Carriage}
        </td>
        <td colSpan={2}>
          <Input
            style={{ width: 100 }}
            maxLength={serverReference.constants.train.maxCarriageLength}
            value={segment.carriage || ""}
            onChange={(_, { value }) => onSegmentChange({ carriage: value })} />
        </td>
      </tr>

      <tr>
        <td className="label">
          {RESX.Flight.Seats}
        </td>
        <td colSpan={2}>
          <Input
            fluid
            maxLength={serverReference.constants.train.maxSeatsLength}
            value={segment.seats || ""}
            onChange={(_, { value }) => onSegmentChange({ seats: value })} />
        </td>
      </tr>

      <tr>
        <td className="label">
          {RESX.Flight.TravelClass}
        </td>
        <td colSpan={2}>
          <BaseReactSelectDropdown
            excludeKeys={[]}
            options={serverReference.trainTravelClasses}
            getLabel={tc => tc.text}
            getValue={tc => tc.value}
            value={serverReference.trainTravelClasses.find(t => t.value === segment.travelClass)}
            onChange={travelClass => onSegmentChange({ travelClass: travelClass.value })}
            config={{}}
          />
        </td>
      </tr>
    </tbody>
  </table >;
};

interface TimepickerProps {
  onTimeChange: (selectedTimeMoment: moment.Moment) => void;
  value: moment.Moment;
}

const MemoizedTimepicker = React.memo(({ onTimeChange, value }: TimepickerProps) => (
  <TimePicker
    value={value}
    onChange={onTimeChange}
  />
));
MemoizedTimepicker.displayName = "MemoizedTimepicker";

export default SegmentEdit;